import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  IconArrowLeft, IconBrandGoogleDrive, IconCircleCheck, IconAlertTriangle,
  IconExternalLink, IconLoader2, IconPlug, IconPlugOff, IconLock,
} from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";

export default function Integraciones() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const isAdmin = user?.role === "psicologa" && user?.is_admin;

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/drive/status");
      setStatus(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    if (searchParams.get("drive") === "connected") {
      setMessage(lang === "es" ? "Google Drive conectado correctamente." : "Google Drive connected successfully.");
      setTimeout(() => setMessage(""), 4000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setAction("connect"); setError("");
    try {
      const { data } = await api.get("/drive/auth/start");
      window.location.href = data.auth_url;
    } catch (e) { setError(formatApiError(e)); setAction(null); }
  };

  const disconnect = async () => {
    if (!window.confirm(lang === "es" ? "¿Desconectar Google Drive? Los archivos ya subidos quedan en tu Drive." : "Disconnect Google Drive? Existing files stay in your Drive.")) return;
    setAction("disconnect"); setError("");
    try {
      await api.post("/drive/disconnect");
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setAction(null); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString(lang === "es" ? "es-HN" : "en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <>
      <div className="mod-top">
        <button className="btn-ghost btn text-[11px] px-2 py-1" onClick={() => nav("/app")} data-testid="integraciones-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Volver" : "Back"}
        </button>
        <span className="t">{lang === "es" ? "INTEGRACIONES · ADMIN" : "INTEGRATIONS · ADMIN"}</span>
      </div>
      <div className="wrap-app max-w-[860px] py-10">
        <p className="eyebrow">{lang === "es" ? "Almacenamiento externo" : "External storage"}</p>
        <h1 className="font-display font-medium text-[clamp(24px,3vw,32px)] mt-1 mb-2">
          {lang === "es" ? "Integraciones" : "Integrations"}
        </h1>
        <p className="text-muted text-[13.5px] mb-8 max-w-[46em] leading-relaxed">
          {lang === "es"
            ? "Conecta servicios externos para almacenar archivos clínicos fuera del servidor de la plataforma. Los videos de los cursos NO se migran; siguen viviendo en el almacenamiento interno."
            : "Connect external services to store clinical files off-platform. Course videos are NOT migrated — they stay on internal storage."}
        </p>

        {message && (
          <div className="mb-4 p-3 rounded-lg bg-okbg text-oktext text-[13px] flex items-center gap-2" data-testid="integraciones-message">
            <IconCircleCheck size={15} /> {message}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-errbg text-errtext text-[13px] flex items-start gap-2" data-testid="integraciones-error">
            <IconAlertTriangle size={15} className="shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        {/* Google Drive card */}
        <div className="card" data-testid="drive-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-lg bg-teal-soft flex items-center justify-center shrink-0">
                <IconBrandGoogleDrive size={22} className="text-teal-deep" />
              </div>
              <div>
                <h3 className="font-display text-[19px] font-medium">Google Drive</h3>
                <p className="text-tert text-[12.5px] mt-0.5">
                  {lang === "es"
                    ? "Archivos clínicos y de terapia. OAuth institucional — una sola cuenta por institución."
                    : "Clinical & therapy files. Institutional OAuth — one account per institution."}
                </p>
              </div>
            </div>
            <div>
              {loading ? (
                <span className="tag" data-testid="drive-loading"><IconLoader2 size={11} className="animate-spin" /> {lang === "es" ? "Cargando" : "Loading"}</span>
              ) : status?.connected ? (
                <span className="tag" style={{ background: "#E7F3EE", color: "#0A5C3F" }} data-testid="drive-connected-tag">
                  <IconCircleCheck size={11} /> {lang === "es" ? "Conectado" : "Connected"}
                </span>
              ) : status?.configured === false ? (
                <span className="tag" style={{ background: "#F3EDE1", color: "#854F0B" }} data-testid="drive-notconfig-tag">
                  <IconLock size={11} /> {lang === "es" ? "Requiere config .env" : "Requires .env config"}
                </span>
              ) : (
                <span className="tag" data-testid="drive-notconnected-tag">
                  {lang === "es" ? "Sin conectar" : "Not connected"}
                </span>
              )}
            </div>
          </div>

          {status?.connected && (
            <div className="mt-4 pt-4 border-t border-line text-[13px] space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="lbl m-0 w-32">{lang === "es" ? "Cuenta Google" : "Google account"}:</span>
                <span className="font-medium" data-testid="drive-email">{status.google_email || "—"}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="lbl m-0 w-32">{lang === "es" ? "Carpeta destino" : "Target folder"}:</span>
                <a
                  href={`https://drive.google.com/drive/folders/${status.folder_id}`}
                  target="_blank" rel="noreferrer"
                  className="text-teal-deep hover:underline flex items-center gap-1"
                  data-testid="drive-folder-link"
                >
                  Chiessa Institute <IconExternalLink size={11} />
                </a>
              </div>
              <div className="flex items-center gap-2">
                <span className="lbl m-0 w-32">{lang === "es" ? "Conectado el" : "Connected on"}:</span>
                <span className="text-muted">{fmt(status.connected_at)}</span>
              </div>
            </div>
          )}

          {!isAdmin && (
            <p className="mt-4 text-tert text-[12px] italic">
              {lang === "es"
                ? "Sólo Mariela (psicóloga admin) puede conectar/desconectar."
                : "Only Mariela (psicologa admin) can connect/disconnect."}
            </p>
          )}

          {isAdmin && (
            <div className="mt-5 flex items-center gap-2 flex-wrap">
              {status?.connected ? (
                <button
                  className="btn-ghost btn text-[12.5px]"
                  onClick={disconnect}
                  disabled={action === "disconnect"}
                  data-testid="drive-disconnect-btn"
                >
                  {action === "disconnect" ? <IconLoader2 size={13} className="animate-spin" /> : <IconPlugOff size={13} />}
                  {lang === "es" ? "Desconectar" : "Disconnect"}
                </button>
              ) : (
                <button
                  className="btn text-[12.5px]"
                  onClick={connect}
                  disabled={action === "connect" || status?.configured === false}
                  data-testid="drive-connect-btn"
                >
                  {action === "connect" ? <IconLoader2 size={13} className="animate-spin" /> : <IconPlug size={13} />}
                  {lang === "es" ? "Conectar Google Drive" : "Connect Google Drive"}
                </button>
              )}
              {status?.configured === false && (
                <p className="text-tert text-[11.5px] max-w-md leading-snug">
                  {lang === "es"
                    ? "El backend aún no tiene credenciales Google. Contacta a soporte para configurar GOOGLE_CLIENT_ID/SECRET/REDIRECT_URI en .env."
                    : "Backend has no Google credentials yet. Contact support to configure GOOGLE_CLIENT_ID/SECRET/REDIRECT_URI in .env."}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 p-4 rounded-lg bg-white border border-line text-[12px] text-tert leading-relaxed">
          <p className="font-medium text-muted mb-1">{lang === "es" ? "Notas de seguridad" : "Security notes"}</p>
          <ul className="list-disc list-inside space-y-1">
            <li>{lang === "es" ? "El refresh token se cifra en reposo con Fernet." : "Refresh token encrypted at rest with Fernet."}</li>
            <li>{lang === "es" ? "El client_secret nunca se guarda en base de datos." : "Client secret is never stored in the database."}</li>
            <li>{lang === "es" ? "Scope mínimo: drive.file (solo archivos creados por la app)." : "Minimal scope: drive.file (only files created by the app)."}</li>
            <li>{lang === "es" ? "Los videos de cursos NO usan Drive — siguen en el almacenamiento interno." : "Course videos do NOT use Drive — they stay on internal storage."}</li>
          </ul>
        </div>
      </div>
    </>
  );
}
